import styled, { css, keyframes } from 'styled-components';

interface IMenuVisible {
  visible: boolean;
}

const upAnimate = keyframes`
  from {
    opacity: 0;
    transform: translateX(-200px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const blurAnimate = keyframes`
  from {
    opacity: 0;
    width: 100%;
  }
  to {
    opacity: 1;
    width: 40%;
  }
`;

export const Container = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export const Header = styled.div<IMenuVisible>`
  position: absolute;
  padding: 1rem;
  top: 0;
  right: 0;
  width: 100%;
  height: 3.75rem;
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  align-items: center;
  justify-content: end;
  border-bottom: 1px solid ${({ theme }) => theme.colors.tertiary};
  z-index: 2;
  transition: all 0.3s ease-out;

  button#btn-open-menu {
    display: none;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  button.btn-actions {
    background: ${({ theme }) => theme.colors.tertiary};
    border: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 0.5rem;

    & + button {
      margin-left: 0.75rem;
    }
  }

  @media (max-width: 768px) {
    justify-content: space-between;

    button#btn-open-menu {
      display: block;
    }

    button.btn-actions {
      & + button {
        margin-left: 0.5rem;
      }
    }
  }
`;

export const Menu = styled.div<IMenuVisible>`
  background: ${({ theme }) => theme.colors.bg};
  width: 19%;
  height: 100%;
  position: fixed;
  z-index: 3;
  border-right: 1px solid ${({ theme }) => theme.colors.tertiary};
  transition: all 0.3s ease-out;

  @media (max-width: 768px) {
    top: 0;
    width: 100%;
    background: transparent;
    z-index: 0;

    ${({ visible }) =>
      visible &&
      css`
        z-index: 3;
        &::after {
          content: '';
          position: absolute;
          height: 100vh;
          background: rgba(1, 1, 1, 0.1);
          animation: ${blurAnimate} 400ms ease forwards;
          right: 0;
          top: 0;
          width: 40%;
        }
      `}
  }
`;

export const Logo = styled.div`
  display: flex;
  padding: 0.9rem 1.25rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.tertiary};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;

  img {
    height: 30px;
  }

  button.btn-actions-mobile {
    display: none;
    background: transparent;
    border: 0;
    color: ${({ theme }) => theme.colors.textPrimary};
    font-size: 1.5rem;
  }

  @media (max-width: 768px) {
    button.btn-actions-desktop {
      display: none;
    }

    button.btn-actions-mobile {
      display: block;
    }
  }
`;

export const Navbar = styled.div<IMenuVisible>`
  height: 100%;
  display: flex;

  ul {
    li {
      padding-left: 0.5rem;
    }
  }

  ul li a,
  button.btn-menu {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: start;
    font-size: 1rem;
    background: transparent;
    border: 0;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: 400;
    border-right: 2px solid transparent;

    svg {
      font-size: 1.25rem;
      margin-right: 0.5rem;
    }

    &:hover {
      color: ${({ theme }) => theme.colors.textPrimary};
    }
  }

  ul li a {
    padding: 0.75rem;
  }

  button.btn-menu {
    padding: 0.75rem 0.75rem 0.75rem 1.25rem;
  }

  .selected {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textPrimary};
    border-color: ${({ theme }) => theme.colors.secondary};
  }

  @media (max-width: 768px) {
    display: ${({ visible }) => (visible ? 'flex' : 'none')};
    animation: ${upAnimate} 400ms ease forwards;
    height: 100vh;
    position: absolute;
    width: 60%;
    top: 0;
    background: ${({ theme }) => theme.colors.bg};
    border-right: 1px solid ${({ theme }) => theme.colors.tertiary};

    ul {
      margin-top: 15px;
    }
  }
`;

export const Wrapper = styled.div`
  flex-grow: 1;
  width: 79.5%;
  position: absolute;
  top: 72px;
  right: 9px;
  padding-bottom: 1rem;

  @media (max-width: 768px) {
    width: 100%;
    top: 60px;
    right: 0;
    margin-top: 2px;
  }
`;

export const Content = styled.div`
  padding: 1rem;
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.tertiary};
  background: ${({ theme }) => theme.colors.bg};

  h4 {
    color: ${({ theme }) => theme.colors.textPrimary};
    margin-bottom: 0;

    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
`;
